import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, ToggleLeft, ToggleRight, Save, X, ChefHat, Fish, Wine, Image, Trash2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

import { API_BASE } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';

interface Product {
  id: number;
  category: 'lamb' | 'beef' | 'seafood' | 'drinks';
  productCode: string | null;
  name: string;
  description: string | null;
  price: number | null;
  originalPrice: number | null;
  isActive: number;
  stockQuantity: number;
  sortOrder: number;
  imageUrl: string | null;
  maxSelect: number;
  isApproved: number;
  supplierId?: number | null;
  supplier?: { id?: number; displayName?: string; supplierTag?: string; label?: string } | null;
}

function toSnakeProductBody(obj: Partial<Product>): Record<string, unknown> {
  return {
    category: obj.category,
    product_code: obj.productCode,
    name: obj.name,
    description: obj.description,
    price: obj.price,
    original_price: obj.originalPrice,
    is_active: obj.isActive,
    stock_quantity: obj.stockQuantity,
    sort_order: obj.sortOrder,
    image_url: obj.imageUrl,
    max_select: obj.maxSelect,
  };
}

interface MediaItem {
  key: string;
  name: string;
  size: number;
  uploaded: string;
  url: string;
}

const categoryLabels: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  lamb: { label: '羊肉', icon: ChefHat, color: 'text-orange-600 bg-orange-50' },
  beef: { label: '牛肉', icon: ChefHat, color: 'text-red-700 bg-red-50' },
  seafood: { label: '海鮮', icon: Fish, color: 'text-blue-600 bg-blue-50' },
  drinks: { label: '酒水', icon: Wine, color: 'text-purple-600 bg-purple-50' },
};

const AdminProducts: React.FC = () => {
  const navigate = useNavigate();
  const { can, user } = usePermissions();
  const canWriteProducts = can(PERMISSIONS.PRODUCTS_WRITE);
  const isSupplier = user?.role === 'supplier';
  const isAdmin = !isSupplier; // missing role falls back to admin
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusTab, setStatusTab] = useState<'all' | 'pending' | 'active'>('all');
  const [activeTab, setActiveTab] = useState<'all' | 'lamb' | 'beef' | 'seafood' | 'drinks'>('all');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Product>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    category: 'beef',
    name: '',
    description: '',
    price: null,
    isActive: 1,
    stockQuantity: 50,
    sortOrder: 0,
  });

  const token = localStorage.getItem('admin_token');

  const fetchProducts = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const query = statusTab !== 'all' ? `?status=${statusTab}` : '';
      const res = await fetch(`${API_BASE}/api/public/admin/products${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setProducts(await res.json());
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    } catch (e) {
      setError('獲取產品失敗');
    } finally {
      setLoading(false);
    }
  }, [token, statusTab, navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchProducts();
  }, [token, navigate, fetchProducts]);

  const fetchMedia = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMediaList(await res.json());
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    } catch (e) {
      setError('獲取媒體庫失敗');
    }
  };

  // Extract original filename from media key like "media/1782221816202_WhatsApp_Image_xxx.jpg"
  const extractOriginalName = (key: string): string => {
    const fileName = key.replace(/^media\//, '');
    const match = fileName.match(/^(\d+)_(.+?)(\.[^.]+)?$/);
    if (match) {
      return match[2].replace(/_/g, ' ');
    }
    return fileName.replace(/\.[^.]+$/, '').replace(/_/g, ' ');
  };

  const usedImageUrls = new Set(products.map(p => p.imageUrl).filter(Boolean));
  const availableMedia = mediaList.filter(m => !usedImageUrls.has(m.url));

  const handleToggle = async (id: number, current: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/products/${id}/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: !current }),
      });
      if (res.ok) fetchProducts();
    } catch (e) {
      setError('操作失敗');
    }
  };

  const handleApprove = async (id: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/products/${id}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchProducts();
      } else {
        setError('審批失敗');
      }
    } catch (e) {
      setError('審批失敗');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定刪除此產品？')) return;
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/products/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchProducts();
      } else {
        setError('刪除失敗');
      }
    } catch (e) {
      setError('刪除失敗');
    }
  };

  const handleEdit = (product: Product) => {
    setEditingId(product.id);
    setEditForm({ ...product });
  };

  const handleSave = async () => {
    if (!editForm.name) return;
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/products/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(toSnakeProductBody(editForm)),
      });
      if (res.ok) {
        setEditingId(null);
        fetchProducts();
      }
    } catch (e) {
      setError('保存失敗');
    }
  };

  const handleAdd = async () => {
    if (!newProduct.name) return;
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(toSnakeProductBody(newProduct)),
      });
      if (res.ok) {
        setShowAddForm(false);
        setNewProduct({ category: 'beef', name: '', description: '', price: null, isActive: 1, stockQuantity: 50, sortOrder: 0 });
        fetchProducts();
      }
    } catch (e) {
      setError('創建失敗');
    }
  };

  const sortedProducts = [...products].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  const filteredProducts = activeTab === 'all'
    ? sortedProducts
    : sortedProducts.filter(p => p.category === activeTab);

  const statusTabs: { key: typeof statusTab; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'pending', label: '待審批' },
    { key: 'active', label: '已上架' },
  ];

  const categoryTabs: { key: typeof activeTab; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'lamb', label: '羊肉' },
    { key: 'beef', label: '牛肉' },
    { key: 'seafood', label: '海鮮' },
    { key: 'drinks', label: '酒水' },
  ];

  const renderStatusBadge = (product: Product) => {
    if (product.isActive) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">已上架</span>;
    }
    if (product.supplierId != null && product.isApproved === 0) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">待審批</span>;
    }
    return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">已下架</span>;
  };

  return (
    <AdminLayout currentPage="products">
      <div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {/* Status tabs (admin/super_admin only) */}
        {isAdmin && (
          <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 mb-4 rounded-t-lg">
            <nav className="flex space-x-1 px-2" aria-label="Status tabs">
              {statusTabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setStatusTab(tab.key)}
                  className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                    statusTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Category tabs */}
        <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 mb-6 rounded-t-lg">
          <nav className="flex space-x-1 px-2" aria-label="Category tabs">
            {categoryTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Add Button */}
        {canWriteProducts && (
          <div className="mb-6">
            <button
              onClick={() => {
                if (!showAddForm) {
                  const maxOrder = products.reduce((max, p) => Math.max(max, p.sortOrder || 0), 0);
                  setNewProduct({
                    category: 'beef',
                    name: '',
                    description: '',
                    price: null,
                    isActive: 1,
                    stockQuantity: 50,
                    sortOrder: maxOrder + 10,
                  });
                  fetchMedia();
                  setShowMediaPicker(false);
                }
                setShowAddForm(!showAddForm);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              {showAddForm ? '取消' : '新增產品'}
            </button>
          </div>
        )}

        {/* Add Form */}
        {showAddForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6 border-l-4 border-blue-500">
            <h3 className="text-lg font-bold dark:text-white">新增產品</h3>
            {isSupplier && (
              <p className="text-sm text-orange-600 mt-1">修改後將重新進入審批</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">類別</label>
                <select
                  value={newProduct.category}
                  onChange={e => setNewProduct({ ...newProduct, category: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                >
                  <option value="lamb">羊肉</option>
                  <option value="beef">牛肉</option>
                  <option value="seafood">海鮮</option>
                  <option value="drinks">酒水</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">名稱</label>
                <input
                  value={newProduct.name}
                  onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="產品名稱"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">價格</label>
                <input
                  type="number"
                  value={newProduct.price || ''}
                  onChange={e => setNewProduct({ ...newProduct, price: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="HK$"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
                <input
                  value={newProduct.description || ''}
                  onChange={e => setNewProduct({ ...newProduct, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="產品描述"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序</label>
                <input
                  type="number"
                  value={newProduct.sortOrder || 0}
                  onChange={e => setNewProduct({ ...newProduct, sortOrder: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">庫存</label>
                <input
                  type="number"
                  value={newProduct.stockQuantity ?? ''}
                  onChange={e => setNewProduct({ ...newProduct, stockQuantity: e.target.value ? parseInt(e.target.value) : 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="數量"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">圖片 URL</label>
                <input
                  value={newProduct.imageUrl || ''}
                  onChange={e => setNewProduct({ ...newProduct, imageUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="https://...（可從媒體庫複製）"
                />
                <button
                  type="button"
                  onClick={() => setShowMediaPicker(!showMediaPicker)}
                  className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <Image size={16} />
                  {showMediaPicker ? '隱藏媒體庫' : `從媒體庫選擇圖片 (${availableMedia.length})`}
                </button>

                {showMediaPicker && (
                  <div className="mt-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
                    {availableMedia.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {mediaList.length === 0 ? '媒體庫為空，請先上傳圖片。' : '所有圖片已被使用。'}
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 max-h-64 overflow-y-auto">
                        {availableMedia.map(item => {
                          const originalName = extractOriginalName(item.key);
                          return (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => {
                                setNewProduct({
                                  ...newProduct,
                                  imageUrl: item.url,
                                  name: originalName,
                                });
                                setShowMediaPicker(false);
                              }}
                              className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              title={originalName}
                            >
                              <img
                                src={item.url}
                                alt={originalName}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                {originalName}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">創建</button>
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">取消</button>
            </div>
          </div>
        )}

        {/* Products Table */}
        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">加載中...</div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">排序</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">類別</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">編號</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">名稱</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">描述</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">價格</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">庫存</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">圖片</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">狀態</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredProducts.map(product => {
                  const cat = categoryLabels[product.category] || categoryLabels.beef;
                  const CatIcon = cat.icon;
                  const isEditing = editingId === product.id;
                  const isPending = product.supplierId != null && product.isActive === 0 && product.isApproved === 0;

                  return (
                    <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.sortOrder ?? 0}
                            onChange={e => setEditForm({ ...editForm, sortOrder: parseInt(e.target.value) || 0 })}
                            className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-bold text-gray-600 dark:text-gray-300">
                            {product.sortOrder || 0}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <select
                            value={editForm.category || 'beef'}
                            onChange={e => setEditForm({ ...editForm, category: e.target.value as Product['category'] })}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          >
                            <option value="lamb">羊肉</option>
                            <option value="beef">牛肉</option>
                            <option value="seafood">海鮮</option>
                            <option value="drinks">酒水</option>
                          </select>
                        ) : (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${cat.color}`}>
                            <CatIcon size={14} />
                            {cat.label}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {isEditing ? (
                          <input
                            value={editForm.productCode || ''}
                            onChange={e => setEditForm({ ...editForm, productCode: e.target.value })}
                            className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                            placeholder="編號"
                          />
                        ) : (
                          <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs">{product.productCode || '-'}</code>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div>
                            <input
                              value={editForm.name || ''}
                              onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                            />
                            {isSupplier && (
                              <p className="text-xs text-orange-600 mt-1">修改後將重新進入審批</p>
                            )}
                          </div>
                        ) : (
                          <div>
                            <span className="font-medium text-gray-900 dark:text-white">{product.name}</span>
                            {product.supplierId != null && product.supplier && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{product.supplier.displayName || product.supplier.label}</div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {isEditing ? (
                          <input
                            value={editForm.description || ''}
                            onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          product.description || '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.price || ''}
                            onChange={e => setEditForm({ ...editForm, price: e.target.value ? parseInt(e.target.value) : null })}
                            className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          product.price ? `HK$${product.price}` : '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.stockQuantity ?? 0}
                            onChange={e => setEditForm({ ...editForm, stockQuantity: parseInt(e.target.value) || 0 })}
                            className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          <span className={`font-medium ${
                            product.stockQuantity === 0 ? 'text-red-600' :
                            product.stockQuantity < 15 ? 'text-orange-500' : 'text-green-600'
                          }`}>
                            {product.stockQuantity}
                            {product.stockQuantity === 0 && <span className="ml-1 text-xs">(缺貨)</span>}
                            {product.stockQuantity > 0 && product.stockQuantity < 15 && <span className="ml-1 text-xs">(即將售罄)</span>}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isEditing ? (
                          <input
                            value={editForm.imageUrl || ''}
                            onChange={e => setEditForm({ ...editForm, imageUrl: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                            placeholder="圖片URL"
                          />
                        ) : (
                          product.imageUrl ? (
                            <img src={product.imageUrl} alt={product.name} className="w-10 h-10 object-cover rounded" />
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                          )
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!isEditing && renderStatusBadge(product)}
                      </td>
                      <td className="px-4 py-3">
                        {canWriteProducts ? (
                          isEditing ? (
                            <div className="flex gap-2">
                              <button onClick={handleSave} className="text-green-600 hover:text-green-800"><Save size={18} /></button>
                              <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                              {!isSupplier && !isPending && (
                                <button
                                  onClick={() => handleToggle(product.id, product.isActive)}
                                  className={`flex items-center gap-1 text-sm font-medium ${product.isActive ? 'text-green-600' : 'text-gray-400'}`}
                                  title={product.isActive ? '停用' : '啟用'}
                                >
                                  {product.isActive ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                                </button>
                              )}
                              <button onClick={() => handleEdit(product)} className="text-blue-600 hover:text-blue-800" title="編輯">
                                <Edit2 size={18} />
                              </button>
                              {isAdmin && isPending && (
                                <button
                                  onClick={() => handleApprove(product.id)}
                                  className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                >
                                  批准上架
                                </button>
                              )}
                              {(isAdmin || !product.isActive) && (
                                <button
                                  onClick={() => handleDelete(product.id)}
                                  className="text-red-600 hover:text-red-800"
                                  title="刪除"
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          )
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredProducts.length === 0 && (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">沒有產品</div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
